import { type ApplicationTypes, type MetalworksApiClient, createMetalworksApiClient } from 'metalworks-domain'
import { type FC, type PropsWithChildren, createContext, useContext, useEffect, useMemo } from 'react'

import type { Session } from '@salsita-ai/platform-project-sdk/domain'
import { useLogger } from '@salsita-ai/platform-project-sdk/react'

import { configuratorInterface } from './configurator-interface.js'

const getQuoteIdStorageKey = (configurationId: string) => `metalworks-quote-id-${configurationId}`
const getQuoteLineIdStorageKey = (configurationId: string) => `metalworks-quote-line-id-${configurationId}`
const toNullableNumber = (value: string | null) => (value ? Number(value) : null)

export const setQuoteId = (configurationId: string, quoteId: number) => {
  localStorage.setItem(getQuoteIdStorageKey(configurationId), String(quoteId))
}

export const setQuoteLineId = (configurationId: string, quoteLineId: number) => {
  localStorage.setItem(getQuoteLineIdStorageKey(configurationId), String(quoteLineId))
}

export const clearQuoteId = (configurationId: string) => {
  localStorage.removeItem(getQuoteIdStorageKey(configurationId))
}

export const clearQuoteLineId = (configurationId: string) => {
  localStorage.removeItem(getQuoteLineIdStorageKey(configurationId))
}

export const getQuoteId = (configurationId: string) =>
  toNullableNumber(localStorage.getItem(getQuoteIdStorageKey(configurationId)))

export const getQuoteLineId = (configurationId: string) =>
  toNullableNumber(localStorage.getItem(getQuoteLineIdStorageKey(configurationId)))

const MetalworksApiContext = createContext<MetalworksApiClient | null>(null)

export const useMetalworksApi = () => {
  const api = useContext(MetalworksApiContext)

  if (!api) {
    throw new Error('useMetalworksApi must be used within a MetalworksApiProvider')
  }

  return api
}

export const createAuthenticatedMetalworksApi = (session: Session<ApplicationTypes.SessionData>) => {
  const sessionPayload = session.sessionPayload
  const metalworksAccessToken = sessionPayload?.data?.accessToken

  const isInvalidAccessToken =
    !metalworksAccessToken ||
    metalworksAccessToken === phoenix.runtimeConfig.FAKE_AUTH_USER_TOKEN ||
    metalworksAccessToken === phoenix.runtimeConfig.FAKE_AUTH_ADMIN_TOKEN

  return createMetalworksApiClient(
    phoenix.runtimeConfig.METALWORKS_API_URL,
    isInvalidAccessToken ? null : metalworksAccessToken
  )
}

export const MetalworksApiProvider: FC<PropsWithChildren> = ({ children }) => {
  const logger = useLogger()
  const session = configuratorInterface.useSession()
  const sessionService = configuratorInterface.useSessionService()
  const metalworksApi = useMemo(() => createAuthenticatedMetalworksApi(session), [session])

  const exp = session.sessionPayload?.exp

  useEffect(() => {
    const expiresIn = exp ? exp - Date.now() / 1000 : null

    if (expiresIn === null || !Number.isFinite(expiresIn)) {
      return
    }

    // Refresh the session slightly earlier to fix potential clock skew issues
    // and to give some extra time to lambda requests to finish.
    const earlyOffset = 60
    const timeoutMs = Math.max(expiresIn - earlyOffset, 30) * 1000

    const refreshTimeout = window.setTimeout(() => {
      sessionService.refreshSession().catch(err => {
        if (err instanceof Error) {
          logger.error('Failed to refresh session', {}, err)
        } else {
          logger.error('Failed to refresh session', { error: err })
        }
      })
    }, timeoutMs)

    return () => {
      window.clearTimeout(refreshTimeout)
    }
  }, [exp, logger, sessionService])

  return <MetalworksApiContext.Provider value={metalworksApi}>{children}</MetalworksApiContext.Provider>
}
